import type { PresentationMode, SlotGrid } from '../rendering/types';
import { createScatterAnticipationPlan } from './scatter-anticipation';

export interface ReelStopTiming {
  readonly reel: number;
  readonly atMs: number;
  readonly revealAtMs?: number;
  readonly anticipationAtMs?: number;
  readonly anticipated: boolean;
  readonly scatterStage: 0 | 1 | 2 | 3;
}

export interface SpinTimeline {
  readonly mode: PresentationMode;
  readonly turbo: boolean;
  readonly reducedMotion: boolean;
  readonly shuffleAtMs?: number;
  readonly dealCompleteAtMs?: number;
  readonly reelStops: readonly ReelStopTiming[];
  readonly settleCompleteAtMs: number;
}

export interface SpinTimelineInput {
  readonly mode: PresentationMode;
  readonly turbo: boolean;
  readonly reducedMotion: boolean;
  readonly grid: SlotGrid;
}

function createReducedMotionTimeline(input: SpinTimelineInput): SpinTimeline {
  const anticipation = createScatterAnticipationPlan(input.grid);
  return {
    mode: input.mode,
    turbo: input.turbo,
    reducedMotion: true,
    reelStops: anticipation.map((entry) => ({
      reel: entry.reel,
      atMs: entry.reel * 24,
      ...(input.mode === 'cards' ? { revealAtMs: entry.reel * 24 } : {}),
      anticipated: false,
      scatterStage: entry.scatterStageAtStop,
    })),
    settleCompleteAtMs: 120,
  };
}

function createCardTimeline(input: SpinTimelineInput): SpinTimeline {
  const plan = createScatterAnticipationPlan(input.grid);
  const deal = input.turbo ? 150 : 250;
  const flip = input.turbo ? 150 : 260;
  const anticipation = input.turbo ? 170 : 390;
  const settle = input.turbo ? 60 : 100;
  const emptyPause = input.turbo ? 45 : 80;
  let cursor = deal;
  const stops = plan.map((entry) => {
    const anticipationAtMs = entry.anticipated ? cursor : undefined;
    if (entry.anticipated) cursor += anticipation;
    const revealAtMs = cursor;
    cursor += flip;
    const atMs = cursor;
    cursor += settle || emptyPause;
    return {
      reel: entry.reel,
      revealAtMs,
      atMs,
      ...(anticipationAtMs === undefined ? {} : { anticipationAtMs }),
      anticipated: entry.anticipated,
      scatterStage: entry.scatterStageAtStop,
    };
  });
  return {
    mode: 'cards', turbo: input.turbo, reducedMotion: false,
    shuffleAtMs: 0, dealCompleteAtMs: deal, reelStops: stops,
    settleCompleteAtMs: cursor,
  };
}

function createPerformanceTimeline(input: SpinTimelineInput): SpinTimeline {
  const plan = createScatterAnticipationPlan(input.grid);
  const base = input.turbo ? 420 : 920;
  const stagger = input.turbo ? 90 : 170;
  const extension = input.turbo ? 180 : 520;
  const focusLead = input.turbo ? 220 : 420;
  const settle = input.turbo ? 90 : 170;
  const stops = plan.map((entry) => {
    const atMs = base + entry.reel * stagger + (entry.anticipated ? extension : 0);
    return {
      reel: entry.reel,
      atMs,
      ...(entry.anticipated ? { anticipationAtMs: Math.max(80, atMs - focusLead) } : {}),
      anticipated: entry.anticipated,
      scatterStage: entry.scatterStageAtStop,
    };
  });
  return {
    mode: 'performance', turbo: input.turbo, reducedMotion: false,
    reelStops: stops,
    settleCompleteAtMs: Math.max(...stops.map((stop) => stop.atMs)) + settle,
  };
}

export function createSpinTimeline(input: SpinTimelineInput): SpinTimeline {
  if (input.reducedMotion) return createReducedMotionTimeline(input);
  return input.mode === 'cards' ? createCardTimeline(input) : createPerformanceTimeline(input);
}
